import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import { publicApi, bookingApi } from '../utils/api';
import { PHONE_PREFIXES, DURATION_OPTIONS } from '../utils/constants';
import '../i18n';

// ── LANGUAGE SWITCHER ──
function LangSwitcher() {
  const { i18n } = useTranslation();
  const langs = [
    { code: 'it', flag: '🇮🇹' }, { code: 'en', flag: '🇬🇧' },
    { code: 'fr', flag: '🇫🇷' }, { code: 'de', flag: '🇩🇪' },
    { code: 'es', flag: '🇪🇸' },
  ];
  return (
    <div style={{ display: 'flex', gap: '0.3rem' }}>
      {langs.map(l => (
        <button key={l.code}
          onClick={() => { i18n.changeLanguage(l.code); localStorage.setItem('brf_lang', l.code); }}
          title={l.code.toUpperCase()}
          style={{ background: i18n.language === l.code ? 'rgba(39,174,96,0.2)' : 'transparent', border: i18n.language === l.code ? '1px solid rgba(39,174,96,0.4)' : '1px solid transparent', borderRadius: '6px', padding: '0.2rem 0.45rem', cursor: 'pointer', fontSize: '0.95rem', transition: 'all 0.2s' }}>
          {l.flag}
        </button>
      ))}
    </div>
  );
}

// ── NAV ──
function Nav({ t }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);
  return (
    <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem 2.5rem', background: scrolled ? 'rgba(5,16,10,0.97)' : 'rgba(5,16,10,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(39,174,96,0.12)', transition: 'all 0.3s' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: '#fff' }}>
        Bike Rent <span style={{ color: 'var(--green)' }}>Florence</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
        {['bikes', 'where'].map(k => (
          <a key={k} href={`#${k}`} style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '0.82rem', fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'color 0.2s' }}
            onMouseEnter={e => e.target.style.color = '#27AE60'}
            onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.6)'}>
            {t(`nav.${k}`)}
          </a>
        ))}
        <LangSwitcher />
        <a href="#prenota" className="btn-primary" style={{ padding: '0.5rem 1.4rem', fontSize: '0.82rem', animation: 'none' }}>
          {t('nav.bookNow')}
        </a>
      </div>
    </nav>
  );
}

// ── HERO ANIMATO ──
function Hero({ t }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    const stars = Array.from({ length: 100 }, () => ({
      x: Math.random(), y: Math.random() * 0.62,
      r: Math.random() * 1.4 + 0.3,
      phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.7 + 0.3,
      green: Math.random() < 0.12,
    }));
    const clouds = [
      { x: 0.08, y: 0.07, w: 0.12, h: 0.032, spd: 0.00012 },
      { x: 0.4,  y: 0.05, w: 0.09, h: 0.026, spd: 0.00009 },
      { x: 0.7,  y: 0.08, w: 0.11, h: 0.030, spd: 0.00011 },
    ];
    let t2 = 0;
    function resize() { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; }
    resize();
    window.addEventListener('resize', resize);
    function draw() {
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = '#05100a'; ctx.fillRect(0, 0, W, H);
      const gr = ctx.createRadialGradient(W/2, H*0.72, 0, W/2, H*0.72, W*0.45);
      gr.addColorStop(0, 'rgba(39,174,96,0.09)'); gr.addColorStop(1, 'transparent');
      ctx.fillStyle = gr; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#d4c87a'; ctx.beginPath(); ctx.arc(W*0.84, H*0.13, H*0.04, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle = '#0e2018'; ctx.beginPath(); ctx.arc(W*0.84+H*0.016, H*0.13-H*0.008, H*0.034, 0, Math.PI*2); ctx.fill();
      stars.forEach(s => {
        const a = 0.25 + 0.6 * (0.5 + 0.5 * Math.sin(t2 * s.speed + s.phase));
        ctx.fillStyle = s.green ? `rgba(39,174,96,${a})` : `rgba(255,255,255,${a})`;
        ctx.beginPath(); ctx.arc(s.x * W, s.y * H, s.r, 0, Math.PI*2); ctx.fill();
      });
      clouds.forEach(c => {
        c.x -= c.spd; if (c.x + c.w < 0) c.x = 1.05;
        ctx.fillStyle = 'rgba(13,32,20,0.7)';
        ctx.beginPath();
        ctx.ellipse(c.x*W + c.w*W*0.5, c.y*H, c.w*W*0.5, c.h*H, 0, 0, Math.PI*2);
        ctx.fill();
      });
      const shimmer = 0.5 + 0.5 * Math.sin(t2 * 1.2);
      ctx.fillStyle = `rgba(39,174,96,${0.06 + shimmer*0.04})`;
      ctx.beginPath(); ctx.ellipse(W/2, H-108, W*0.22, 10, 0, 0, Math.PI*2); ctx.fill();
      t2 += 0.018;
      animId = requestAnimationFrame(draw);
    }
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <section id="hero" style={{ minHeight: '100vh', background: '#05100a', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
      {/* Skyline Firenze */}
      <div style={{ position: 'absolute', bottom: 90, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none', zIndex: 1 }}>
        <svg width="1000" height="200" viewBox="0 0 1000 200" fill="none">
          <ellipse cx="130" cy="185" rx="230" ry="50" fill="#061508"/>
          <ellipse cx="870" cy="188" rx="210" ry="45" fill="#061508"/>
          <rect x="160" y="68" width="40" height="132" fill="#0c2414"/>
          <rect x="171" y="56" width="18" height="16" fill="#0c2414"/>
          <rect x="177" y="44" width="8" height="16" fill="#27AE60" opacity=".8"/>
          <rect x="250" y="80" width="28" height="120" fill="#0c2414"/>
          <rect x="257" y="70" width="14" height="12" fill="#27AE60" opacity=".6"/>
          <rect x="380" y="92" width="130" height="108" fill="#0e2a18"/>
          <path d="M380 92 Q410 54 445 42 Q480 54 510 92Z" fill="#0e2a18"/>
          <line x1="445" y1="42" x2="410" y2="92" stroke="#27AE60" strokeWidth=".7" opacity=".3"/>
          <line x1="445" y1="42" x2="480" y2="92" stroke="#27AE60" strokeWidth=".7" opacity=".3"/>
          <rect x="438" y="26" width="12" height="20" fill="#27AE60" opacity=".9"/>
          <circle cx="444" cy="22" r="8" fill="#27AE60"/>
          <rect x="522" y="70" width="32" height="130" fill="#0c2414"/>
          <rect x="530" y="62" width="16" height="12" fill="#27AE60" opacity=".6"/>
          <rect x="590" y="96" width="68" height="104" fill="#0c2414"/>
          <path d="M590 96 L624 76 L658 96Z" fill="#0c2414"/>
          <rect x="620" y="68" width="8" height="12" fill="#27AE60" opacity=".5"/>
          <rect x="100" y="168" width="240" height="22" fill="#091c0e"/>
          {[102,122,142,162,182,202,222,242,262,282].map(x => (
            <rect key={x} x={x} y={157} width={16} height={12} fill="#0c2414"/>
          ))}
        </svg>
      </div>
      {/* Suolo e strada */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 90, background: '#071a0d', zIndex: 2 }}/>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 40, background: '#0b1e10', zIndex: 3, overflow: 'hidden' }}>
        {Array.from({length:14},(_,i) => (
          <div key={i} style={{ position:'absolute', bottom:16, left:i*88, width:48, height:4, background:'rgba(39,174,96,0.22)', borderRadius:2, animation:`dashScroll 1s linear infinite`, animationDelay:`${-i*0.088}s` }}/>
        ))}
      </div>
      {/* Bicicletta animata */}
      <div style={{ position:'absolute', bottom:40, left:'50%', transform:'translateX(-50%)', zIndex:4, animation:'bikeBob 0.45s ease-in-out infinite alternate' }}>
        {[-68,-52,-42].map((left,i) => (
          <div key={i} style={{ position:'absolute', height:i===0?2:1.5, background:'#27AE60', borderRadius:2, bottom:i===0?28:i===1?42:16, left, width:i===0?55:i===1?35:22, animation:'trailFade 0.5s ease-out infinite', animationDelay:`${i*0.18}s` }}/>
        ))}
        <svg width="180" height="112" viewBox="0 0 180 112">
          <g style={{transformOrigin:'38px 80px', animation:'wheelSpin 0.55s linear infinite'}}>
            <circle cx="38" cy="80" r="28" fill="none" stroke="#27AE60" strokeWidth="3"/>
            <circle cx="38" cy="80" r="6" fill="#27AE60"/>
            <line x1="38" y1="52" x2="38" y2="108" stroke="#27AE60" strokeWidth="1.8" opacity=".5"/>
            <line x1="10" y1="80" x2="66" y2="80" stroke="#27AE60" strokeWidth="1.8" opacity=".5"/>
            <line x1="18" y1="60" x2="58" y2="100" stroke="#27AE60" strokeWidth="1.5" opacity=".35"/>
            <line x1="58" y1="60" x2="18" y2="100" stroke="#27AE60" strokeWidth="1.5" opacity=".35"/>
          </g>
          <g style={{transformOrigin:'142px 80px', animation:'wheelSpin 0.55s linear infinite'}}>
            <circle cx="142" cy="80" r="28" fill="none" stroke="#27AE60" strokeWidth="3"/>
            <circle cx="142" cy="80" r="6" fill="#27AE60"/>
            <line x1="142" y1="52" x2="142" y2="108" stroke="#27AE60" strokeWidth="1.8" opacity=".5"/>
            <line x1="114" y1="80" x2="170" y2="80" stroke="#27AE60" strokeWidth="1.8" opacity=".5"/>
            <line x1="122" y1="60" x2="162" y2="100" stroke="#27AE60" strokeWidth="1.5" opacity=".35"/>
            <line x1="162" y1="60" x2="122" y2="100" stroke="#27AE60" strokeWidth="1.5" opacity=".35"/>
          </g>
          <line x1="38" y1="80" x2="90" y2="46" stroke="#27AE60" strokeWidth="2.8"/>
          <line x1="142" y1="80" x2="90" y2="46" stroke="#27AE60" strokeWidth="2.8"/>
          <line x1="90" y1="46" x2="84" y2="80" stroke="#27AE60" strokeWidth="2.8"/>
          <line x1="84" y1="80" x2="38" y2="80" stroke="#27AE60" strokeWidth="2.8"/>
          <line x1="90" y1="46" x2="114" y2="32" stroke="#27AE60" strokeWidth="2.2"/>
          <line x1="108" y1="28" x2="122" y2="34" stroke="#27AE60" strokeWidth="3.5" strokeLinecap="round"/>
          <circle cx="90" cy="46" r="5" fill="#27AE60"/>
          <g style={{transformOrigin:'84px 80px', animation:'wheelSpin 0.55s linear infinite'}}>
            <line x1="74" y1="80" x2="94" y2="80" stroke="#27AE60" strokeWidth="2"/>
            <rect x="70" y="78" width="8" height="4" rx="2" fill="#27AE60" opacity=".7"/>
            <rect x="90" y="78" width="8" height="4" rx="2" fill="#27AE60" opacity=".7"/>
          </g>
          <line x1="84" y1="46" x2="74" y2="38" stroke="#27AE60" strokeWidth="2"/>
          <rect x="62" y="34" width="24" height="8" rx="4" fill="#1a4a28" stroke="#27AE60" strokeWidth="1.5"/>
          <circle cx="102" cy="22" r="10" fill="#1a4a28" stroke="#27AE60" strokeWidth="1.8"/>
          <path d="M93 26 Q102 14 111 26" fill="#27AE60" opacity=".5"/>
          <line x1="102" y1="32" x2="90" y2="46" stroke="#27AE60" strokeWidth="2.2"/>
          <rect x="136" y="72" width="8" height="12" rx="3" fill="#27AE60" opacity=".8"/>
        </svg>
      </div>
      <div style={{ position:'absolute', width:380, height:380, borderRadius:'50%', background:'rgba(39,174,96,0.07)', top:'50%', left:'50%', marginLeft:-190, marginTop:-190, animation:'glowBreath 2.5s ease-in-out infinite', pointerEvents:'none', zIndex:1 }}/>
      {/* Contenuto */}
      <div style={{ position:'relative', zIndex:5, padding:'2rem', marginBottom:140, animation:'fadeUp 0.9s ease both' }}>
        <div style={{ display:'inline-flex', alignItems:'center', gap:'.5rem', background:'rgba(39,174,96,.12)', border:'1px solid rgba(39,174,96,.3)', color:'#27AE60', fontSize:'.72rem', fontWeight:700, letterSpacing:'.18em', textTransform:'uppercase', padding:'.45rem 1.3rem', borderRadius:'100px', marginBottom:'2rem' }}>
          📍 {t('hero.tag')}
        </div>
        <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(3rem,8vw,7rem)', color:'#fff', lineHeight:.95, marginBottom:'1.5rem', fontWeight:600 }}>
          {t('hero.title')}<br/>
          <em style={{ color:'#27AE60', fontStyle:'italic', display:'block' }}>{t('hero.subtitle')}</em>
        </h1>
        <p style={{ color:'rgba(255,255,255,.45)', fontSize:'1.05rem', fontWeight:300, maxWidth:440, margin:'0 auto 2.5rem', lineHeight:1.75 }}>
          {t('hero.sub')}
        </p>
        <div style={{ display:'flex', gap:'1rem', justifyContent:'center', flexWrap:'wrap' }}>
          <a href="#prenota" className="btn-primary">🚲 {t('hero.cta')}</a>
          <a href="#bikes" className="btn-ghost">{t('hero.discover')} →</a>
        </div>
      </div>
    </section>
  );
}

// ── BIKES ──
function BikesSection({ t, settings, rates, availability }) {
  const getPrice = (type, dur) => {
    const r = rates.find(r => r.bike_type === type && r.duration === dur && r.active !== 0);
    return r ? parseFloat(r.price) : null;
  };

  const bikes = [
    { type: 'classic', icon: '🚲', tag: t('bikes.classic'), name: t('bikes.classic'), desc: t('bikes.classicDesc'), specs: ['Shimano 7v','Cestino incluso','Sella ergonomica','Campanello'], tagBg: '#27AE60', imgBg: 'linear-gradient(135deg,#e8f5ee,#d4edde)' },
    { type: 'ebike',   icon: '⚡', tag: t('bikes.ebike'),   name: t('bikes.ebike'),   desc: t('bikes.ebikeDesc'),   specs: ['250W Bafang','60km autonomia','Display LCD','USB charging'],     tagBg: '#D4A843', imgBg: 'linear-gradient(135deg,#fdf6e3,#f5e8c0)' },
  ];

  return (
    <section id="bikes" style={{ padding:'5rem 1.5rem', background:'var(--cream)' }}>
      <div className="container">
        <div className="section-eyebrow">{t('bikes.eyebrow')}</div>
        <h2 className="section-title">{t('bikes.title')}</h2>
        <p className="section-sub">{t('bikes.sub')}</p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.5rem', marginTop:'3rem' }}>
          {bikes.map(bike => {
            const avail = availability[bike.type] ?? 99;
            const isAvail = avail > 0;
            const photo = settings?.[`photo_${bike.type}`];
            return (
              <div key={bike.type} style={{ background:'#fff', borderRadius:24, overflow:'hidden', boxShadow:'0 2px 20px rgba(0,0,0,.06)', border:`2px solid ${isAvail?'transparent':'rgba(231,76,60,0.2)'}`, transition:'all .35s' }}
                onMouseEnter={e => isAvail && Object.assign(e.currentTarget.style, {borderColor:'#27AE60',boxShadow:'0 12px 40px rgba(39,174,96,.15)',transform:'translateY(-4px)'})}
                onMouseLeave={e => Object.assign(e.currentTarget.style, {borderColor:isAvail?'transparent':'rgba(231,76,60,0.2)',boxShadow:'0 2px 20px rgba(0,0,0,.06)',transform:'translateY(0)'})}>
                <div style={{ height:220, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'7rem', position:'relative', overflow:'hidden', background:bike.imgBg }}>
                  {photo ? <img src={photo} alt={bike.name} style={{ width:'100%',height:'100%',objectFit:'cover',position:'absolute',inset:0 }}/> : <span>{bike.icon}</span>}
                  <div style={{ position:'absolute', top:'1rem', left:'1rem', padding:'.3rem .9rem', borderRadius:'100px', fontSize:'.68rem', fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'#fff', background:bike.tagBg }}>
                    {bike.tag}
                  </div>
                  {!isAvail && <div style={{ position:'absolute', top:'1rem', right:'1rem', background:'rgba(231,76,60,0.9)', color:'#fff', padding:'.3rem .9rem', borderRadius:'100px', fontSize:'.68rem', fontWeight:700 }}>{t('bikes.soldOut')}</div>}
                  {isAvail && avail <= 3 && <div style={{ position:'absolute', top:'1rem', right:'1rem', background:'rgba(212,168,67,0.9)', color:'#fff', padding:'.3rem .9rem', borderRadius:'100px', fontSize:'.68rem', fontWeight:700 }}>Solo {avail}!</div>}
                </div>
                <div style={{ padding:'1.8rem' }}>
                  <div style={{ fontFamily:'var(--font-display)', fontSize:'1.7rem', fontWeight:600, marginBottom:'.4rem' }}>{bike.name}</div>
                  <p style={{ color:'var(--gray)', fontSize:'.9rem', lineHeight:1.65, marginBottom:'1.2rem' }}>{bike.desc}</p>
                  <div style={{ display:'flex', gap:'.6rem', flexWrap:'wrap', marginBottom:'1.4rem' }}>
                    {bike.specs.map(s => <span key={s} style={{ background:'var(--light)', color:'var(--green2)', fontSize:'.72rem', fontWeight:600, padding:'.25rem .7rem', borderRadius:6 }}>{s}</span>)}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'.5rem' }}>
                    {['1h','2h','4h','day'].map(dur => {
                      const price = getPrice(bike.type, dur);
                      if (!price) return null;
                      const l = {'1h':'1h','2h':'2h','4h':'½ giornata','day':'Giornata'};
                      return (
                        <div key={dur} style={{ background:'var(--light)', borderRadius:8, padding:'.5rem .4rem', textAlign:'center' }}>
                          <div style={{ fontSize:'.6rem', color:'var(--gray)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'.2rem' }}>{l[dur]}</div>
                          <div style={{ fontFamily:'var(--font-display)', fontSize:'1.2rem', fontWeight:700, color:'var(--green2)' }}>€{price}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── BOOKING ──
function BookingSection({ t, settings, rates, optionals, availability, cautionSettings }) {
  const [step, setStep] = useState(1);
  const sigRef = useRef(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    time: '09:00', duration: '1h',
    qty_classic: 0, qty_ebike: 0,
    selectedOpts: {},
    firstName:'', lastName:'', email:'',
    prefix:'+39', phone:'',
    docType:'', docFile: null,
    chkContract:false, chkPrivacy:false, chkCaution:false, chkMarketing:false,
    payMethod:'card',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [bookingRef, setBookingRef] = useState('');

  const avail = availability;
  const getPrice = (type, dur) => {
    const r = rates.find(r => r.bike_type === type && r.duration === dur && r.active !== 0);
    return r ? parseFloat(r.price) : 0;
  };

  const totalBikes = form.qty_classic + form.qty_ebike;
  const rentalTotal = form.qty_classic * getPrice('classic', form.duration) + form.qty_ebike * getPrice('ebike', form.duration);
  const optTotal = Object.entries(form.selectedOpts).reduce((acc, [id, active]) => {
    if (!active) return acc;
    const opt = optionals.find(o => o.id === parseInt(id));
    return acc + (opt ? parseFloat(opt.price) * totalBikes : 0);
  }, 0);
  const total = rentalTotal + optTotal;
  const cautionTotal = form.qty_classic * (cautionSettings.classic||100) + form.qty_ebike * (cautionSettings.ebike||200);
  const durLabels = { '1h':t('booking.dur1h'),'2h':t('booking.dur2h'),'4h':t('booking.dur4h'),'day':t('booking.durDay') };

  function chgQty(type, delta) {
    const max = avail[type] ?? 99;
    const k = `qty_${type}`;
    setForm(f => ({ ...f, [k]: Math.max(0, Math.min(max, f[k]+delta)) }));
  }

  function validate(s) {
    const e = {};
    if (s===1 && totalBikes===0) e.bikes='Seleziona almeno una bicicletta';
    if (s===3) {
      if (!form.firstName.trim()) e.firstName='Obbligatorio';
      if (!form.lastName.trim())  e.lastName='Obbligatorio';
      if (!form.email.includes('@')) e.email='Email non valida';
      if (!form.phone.trim())    e.phone='Obbligatorio';
      if (!form.docType)         e.docType='Seleziona tipo documento';
      if (!form.docFile)         e.docFile='Carica documento';
    }
    if (s===4) {
      if (sigRef.current?.isEmpty()) e.signature='Firma obbligatoria';
      if (!form.chkContract || !form.chkPrivacy || !form.chkCaution) e.checks='Accetta tutte le voci obbligatorie';
    }
    setErrors(e);
    return Object.keys(e).length===0;
  }

  function nextStep() { if (validate(step)) setStep(s=>s+1); }

  async function submitBooking() {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('client_name', `${form.firstName} ${form.lastName}`);
      fd.append('client_email', form.email);
      fd.append('client_phone_prefix', form.prefix);
      fd.append('client_phone', form.phone);
      fd.append('client_doc_type', form.docType);
      fd.append('date_start', `${form.date}T${form.time}:00`);
      fd.append('duration', form.duration);
      fd.append('qty_classic', form.qty_classic);
      fd.append('qty_ebike', form.qty_ebike);
      fd.append('signature_data', sigRef.current.toDataURL());
      fd.append('document', form.docFile);
      const selOpts = Object.entries(form.selectedOpts).filter(([,v])=>v).map(([id])=>({id:parseInt(id),quantity:1}));
      fd.append('optionals', JSON.stringify(selOpts));
      const res = await bookingApi.create(fd);
      setBookingRef(res.data.booking_ref);
      setShowSuccess(true);
    } catch(err) {
      alert(err.response?.data?.error || 'Errore. Riprova.');
    } finally { setLoading(false); }
  }

  const stepNames = [t('booking.step1'),t('booking.step2'),t('booking.step3'),t('booking.step4'),t('booking.step5')];
  const s = { panel: { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:20, padding:'2rem', animation:'fadeUp .4s ease' } };

  return (
    <section id="prenota" style={{ padding:'5rem 1.5rem', background:'var(--dark2)' }}>
      <div className="container">
        <div className="section-eyebrow" style={{color:'var(--green)'}}>{t('booking.eyebrow')}</div>
        <h2 className="section-title" style={{color:'#fff'}}>{t('booking.title')}</h2>
        <p className="section-sub" style={{color:'rgba(255,255,255,.4)'}}>{t('booking.sub')}</p>

        {/* Step bar */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:0, margin:'2rem 0 2.5rem', flexWrap:'wrap' }}>
          {stepNames.map((name,i) => {
            const n=i+1; const isA=n===step; const isD=n<step;
            return (
              <React.Fragment key={n}>
                <div onClick={() => isD && setStep(n)}
                  style={{ display:'flex', alignItems:'center', gap:'.5rem', padding:'.5rem 1rem', borderRadius:'100px', fontSize:'.78rem', fontWeight:600, color:isA?'#27AE60':isD?'rgba(255,255,255,.5)':'rgba(255,255,255,.3)', background:isA?'rgba(39,174,96,.15)':'transparent', border:isA?'1px solid rgba(39,174,96,.3)':'1px solid transparent', cursor:isD?'pointer':'default', transition:'all .3s' }}>
                  <span style={{ width:22,height:22,borderRadius:'50%',border:`1px solid ${isA||isD?'#27AE60':'currentColor'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.7rem',background:isD?'#27AE60':'transparent',color:isD?'#fff':'currentColor',flexShrink:0 }}>{isD?'✓':n}</span>
                  {name}
                </div>
                {i<4 && <div style={{width:24,height:1,background:'rgba(255,255,255,.1)'}}/>}
              </React.Fragment>
            );
          })}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:'2rem', alignItems:'start' }}>
          <div>
            {/* STEP 1 */}
            {step===1 && (
              <div style={s.panel}>
                <h3 style={{color:'#fff',marginBottom:'1.5rem',fontFamily:'var(--font-display)',fontSize:'1.2rem'}}>📅 {t('booking.step1')}</h3>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'.9rem',marginBottom:'1rem'}}>
                  <div className="field-group">
                    <label className="field-label">{t('booking.date')}</label>
                    <input className="field-input" type="date" value={form.date} min={new Date().toISOString().split('T')[0]} onChange={e=>setForm(f=>({...f,date:e.target.value}))}/>
                  </div>
                  <div className="field-group">
                    <label className="field-label">{t('booking.startTime')}</label>
                    <input className="field-input" type="time" value={form.time} onChange={e=>setForm(f=>({...f,time:e.target.value}))}/>
                  </div>
                </div>
                <div className="field-group" style={{marginBottom:'1.5rem'}}>
                  <label className="field-label">{t('booking.duration')}</label>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'.6rem'}}>
                    {DURATION_OPTIONS.map(d => {
                      const pc=getPrice('classic',d.value); const pe=getPrice('ebike',d.value);
                      return (
                        <div key={d.value} onClick={()=>setForm(f=>({...f,duration:d.value}))}
                          style={{background:form.duration===d.value?'rgba(39,174,96,.1)':'rgba(255,255,255,.05)',border:`1.5px solid ${form.duration===d.value?'#27AE60':'rgba(255,255,255,.1)'}`,borderRadius:10,padding:'.7rem .4rem',textAlign:'center',cursor:'pointer',transition:'all .2s'}}>
                          <span style={{display:'block',fontSize:'.82rem',fontWeight:600,color:'#fff'}}>{t(`booking.${d.label_key}`)}</span>
                          {pc>0 && <span style={{display:'block',fontSize:'.6rem',color:'rgba(255,255,255,.35)',marginTop:'.2rem'}}>🚲€{pc} ⚡€{pe}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="field-group">
                  <label className="field-label">{t('booking.howMany')}</label>
                  {[{type:'classic',icon:'🚲',label:t('booking.classic')},{type:'ebike',icon:'⚡',label:t('booking.ebike')}].map(b => {
                    const max=avail[b.type]??99; const qty=form[`qty_${b.type}`]; const price=getPrice(b.type,form.duration); const exh=max===0;
                    return (
                      <div key={b.type} style={{display:'flex',alignItems:'center',gap:'.8rem',background:exh?'rgba(231,76,60,.05)':'rgba(255,255,255,.05)',border:`1px solid ${exh?'rgba(231,76,60,.2)':'rgba(255,255,255,.1)'}`,borderRadius:12,padding:'.9rem 1.1rem',marginBottom:'.6rem'}}>
                        <div style={{fontSize:'1.8rem'}}>{b.icon}</div>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:600,color:'#fff',fontSize:'.92rem'}}>{b.label}</div>
                          <div style={{fontSize:'.75rem',color:exh?'var(--red)':'var(--green)',fontWeight:500,marginTop:'.1rem'}}>
                            {exh ? t('booking.notAvailable') : `€${price} · ${max} ${t('bikes.available')}`}
                          </div>
                        </div>
                        {!exh && (
                          <div style={{display:'flex',alignItems:'center',gap:'.6rem'}}>
                            <button onClick={()=>chgQty(b.type,-1)} style={{width:28,height:28,borderRadius:'50%',border:'1px solid rgba(255,255,255,.2)',background:'transparent',color:'#fff',fontSize:'1rem',cursor:'pointer',transition:'all .2s'}}>−</button>
                            <span style={{fontWeight:700,minWidth:18,textAlign:'center',color:'#fff'}}>{qty}</span>
                            <button onClick={()=>chgQty(b.type,1)} disabled={qty>=max} style={{width:28,height:28,borderRadius:'50%',border:'1px solid rgba(255,255,255,.2)',background:'transparent',color:qty>=max?'rgba(255,255,255,.2)':'#fff',fontSize:'1rem',cursor:qty>=max?'not-allowed':'pointer',transition:'all .2s'}}>+</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {errors.bikes && <div className="field-error">{errors.bikes}</div>}
                </div>
                <button className="btn-primary" style={{width:'100%',justifyContent:'center',animation:'none',marginTop:'.5rem'}} onClick={nextStep}>{t('booking.continue')}</button>
              </div>
            )}

            {/* STEP 2 */}
            {step===2 && (
              <div style={s.panel}>
                <h3 style={{color:'#fff',marginBottom:'1.5rem',fontFamily:'var(--font-display)',fontSize:'1.2rem'}}>🎒 {t('booking.optionals')}</h3>
                {optionals.length===0 && <p style={{color:'rgba(255,255,255,.4)',fontSize:'.9rem'}}>Nessun optional disponibile.</p>}
                {optionals.map(opt => {
                  const active=form.selectedOpts[opt.id];
                  return (
                    <div key={opt.id} onClick={()=>setForm(f=>({...f,selectedOpts:{...f.selectedOpts,[opt.id]:!f.selectedOpts[opt.id]}}))}
                      style={{display:'flex',alignItems:'center',justifyContent:'space-between',background:active?'rgba(212,168,67,.06)':'rgba(255,255,255,.04)',border:`1.5px solid ${active?'var(--gold)':'rgba(255,255,255,.08)'}`,borderRadius:12,padding:'.85rem 1rem',marginBottom:'.5rem',cursor:'pointer',transition:'all .2s'}}>
                      <div>
                        <div style={{fontWeight:600,fontSize:'.9rem',color:'#fff'}}>{opt.name}</div>
                        <div style={{fontSize:'.72rem',color:'rgba(255,255,255,.4)',marginTop:'.1rem'}}>{opt.description}</div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:'.8rem'}}>
                        <span style={{color:'var(--gold)',fontWeight:700,fontSize:'.88rem'}}>{opt.price>0?`+€${opt.price}/bici`:'Gratis'}</span>
                        <div style={{width:22,height:22,borderRadius:6,border:`2px solid ${active?'var(--gold)':'rgba(255,255,255,.2)'}`,background:active?'var(--gold)':'transparent',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.75rem',color:active?'#fff':'transparent',transition:'all .2s'}}>✓</div>
                      </div>
                    </div>
                  );
                })}
                <button className="btn-primary" style={{width:'100%',justifyContent:'center',animation:'none',marginTop:'1rem'}} onClick={nextStep}>{t('booking.continue')}</button>
              </div>
            )}

            {/* STEP 3 */}
            {step===3 && (
              <div style={s.panel}>
                <h3 style={{color:'#fff',marginBottom:'1.5rem',fontFamily:'var(--font-display)',fontSize:'1.2rem'}}>👤 {t('booking.yourData')}</h3>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'.9rem'}}>
                  <div className="field-group">
                    <label className="field-label">{t('booking.firstName')} *</label>
                    <input className={`field-input${errors.firstName?' error':''}`} type="text" placeholder="Mario" value={form.firstName} onChange={e=>setForm(f=>({...f,firstName:e.target.value}))}/>
                    {errors.firstName && <div className="field-error">{errors.firstName}</div>}
                  </div>
                  <div className="field-group">
                    <label className="field-label">{t('booking.lastName')} *</label>
                    <input className={`field-input${errors.lastName?' error':''}`} type="text" placeholder="Rossi" value={form.lastName} onChange={e=>setForm(f=>({...f,lastName:e.target.value}))}/>
                    {errors.lastName && <div className="field-error">{errors.lastName}</div>}
                  </div>
                </div>
                <div className="field-group">
                  <label className="field-label">{t('booking.email')} *</label>
                  <input className={`field-input${errors.email?' error':''}`} type="email" placeholder="mario@email.com" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/>
                  {errors.email && <div className="field-error">{errors.email}</div>}
                </div>
                <div className="field-group">
                  <label className="field-label">{t('booking.phone')} *</label>
                  <div style={{display:'flex',gap:'.6rem'}}>
                    <select className="field-input" style={{width:170,flexShrink:0}} value={form.prefix} onChange={e=>setForm(f=>({...f,prefix:e.target.value}))}>
                      {PHONE_PREFIXES.map(p=><option key={p.code} value={p.code}>{p.flag} {p.code} – {p.name}</option>)}
                    </select>
                    <input className={`field-input${errors.phone?' error':''}`} type="tel" placeholder="333 000 0000" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))}/>
                  </div>
                  {errors.phone && <div className="field-error">{errors.phone}</div>}
                </div>
                <div className="field-group">
                  <label className="field-label">{t('booking.docType')} *</label>
                  <select className={`field-input${errors.docType?' error':''}`} value={form.docType} onChange={e=>setForm(f=>({...f,docType:e.target.value}))}>
                    <option value="">— Seleziona —</option>
                    <option value="id">{t('docs.id')}</option>
                    <option value="passport">{t('docs.passport')}</option>
                    <option value="license">{t('docs.license')}</option>
                  </select>
                  {errors.docType && <div className="field-error">{errors.docType}</div>}
                </div>
                <div className="field-group">
                  <label className="field-label">{t('booking.docUpload')} <span style={{color:'var(--green)',fontSize:'.7rem',textTransform:'none',letterSpacing:0}}>{t('booking.docRequired')}</span></label>
                  <div onClick={()=>document.getElementById('doc-upload').click()}
                    style={{border:`2px dashed ${errors.docFile?'var(--red)':form.docFile?'var(--green)':'rgba(255,255,255,.18)'}`,borderRadius:14,padding:'1.5rem',textAlign:'center',cursor:'pointer',transition:'all .3s',background:form.docFile?'rgba(39,174,96,.07)':'rgba(255,255,255,.03)'}}>
                    <div style={{fontSize:'2rem',marginBottom:'.5rem'}}>{form.docFile?'✅':'📤'}</div>
                    <div style={{fontSize:'.88rem',color:form.docFile?'#fff':'rgba(255,255,255,.45)'}}>{form.docFile?form.docFile.name:t('booking.docDrag')}</div>
                    <div style={{fontSize:'.72rem',color:'rgba(255,255,255,.25)',marginTop:'.3rem'}}>{t('booking.docHint')}</div>
                    <input id="doc-upload" type="file" accept="image/*,.pdf" style={{display:'none'}} onChange={e=>setForm(f=>({...f,docFile:e.target.files[0]}))}/>
                  </div>
                  {errors.docFile && <div className="field-error">{errors.docFile}</div>}
                </div>
                <div style={{background:'rgba(212,168,67,.08)',border:'1px solid rgba(212,168,67,.2)',borderRadius:10,padding:'.8rem 1rem',fontSize:'.78rem',color:'rgba(255,255,255,.5)',lineHeight:1.6,marginBottom:'1rem'}}>
                  🔒 Il documento è necessario per identificare il conduttore. I dati vengono trattati ex GDPR e cancellati entro 30 giorni.
                </div>
                <button className="btn-primary" style={{width:'100%',justifyContent:'center',animation:'none'}} onClick={nextStep}>{t('booking.continue')}</button>
              </div>
            )}

            {/* STEP 4 */}
            {step===4 && (
              <div style={s.panel}>
                <h3 style={{color:'#fff',marginBottom:'1.5rem',fontFamily:'var(--font-display)',fontSize:'1.2rem'}}>📄 {t('booking.contract')}</h3>
                <div style={{background:'rgba(255,255,255,.03)',border:'1px solid rgba(255,255,255,.08)',borderRadius:14,padding:'1.2rem',maxHeight:200,overflowY:'auto',marginBottom:'1rem',fontSize:'.78rem',color:'rgba(255,255,255,.45)',lineHeight:1.8}}>
                  <strong style={{color:'rgba(255,255,255,.7)'}}>CONTRATTO DI NOLEGGIO — Bike Rent Florence</strong><br/><br/>
                  <strong>Art. 1</strong> – Il Locatore concede in noleggio le biciclette selezionate per il periodo indicato.<br/>
                  <strong>Art. 2</strong> – Cauzione: pre-autorizzazione di €{cautionTotal} sulla carta. I fondi NON vengono addebitati e tornano disponibili entro 48h dalla restituzione in perfette condizioni.<br/>
                  <strong>Art. 3</strong> – Il Conduttore è responsabile del mezzo dalla consegna alla restituzione. In caso di furto: denuncia entro 24h obbligatoria.<br/>
                  <strong>Art. 4</strong> – Rispettare il Codice della Strada. Non sublocare. Restituire pulito all'orario stabilito.<br/>
                  <strong>Art. 5</strong> – I dati personali vengono trattati ex Reg. UE 2016/679 e cancellati entro 30 giorni.<br/>
                  <strong>Art. 6</strong> – Foro competente: Firenze.
                </div>
                <div className="field-group">
                  <label className="field-label" style={{marginBottom:'.5rem'}}>✍️ {t('booking.signHere')}</label>
                  <div style={{background:'rgba(255,255,255,.04)',border:`1.5px solid ${errors.signature?'var(--red)':'rgba(255,255,255,.12)'}`,borderRadius:14,overflow:'hidden'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'.7rem 1rem',borderBottom:'1px solid rgba(255,255,255,.06)'}}>
                      <span style={{fontSize:'.72rem',color:'rgba(255,255,255,.35)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em'}}>{t('booking.signHint')}</span>
                      <button onClick={()=>sigRef.current.clear()} style={{background:'transparent',border:'none',color:'rgba(255,255,255,.3)',fontSize:'.72rem',cursor:'pointer',fontFamily:'var(--font-body)'}}>✕ {t('booking.signClear')}</button>
                    </div>
                    <SignatureCanvas ref={sigRef} canvasProps={{width:600,height:140,style:{display:'block',width:'100%',cursor:'crosshair'}}} penColor="#27AE60"/>
                    <div style={{textAlign:'center',padding:'.5rem',fontSize:'.72rem',color:'rgba(255,255,255,.2)'}}>{t('booking.signHint')}</div>
                  </div>
                  {errors.signature && <div className="field-error">{errors.signature}</div>}
                </div>
                {[
                  {key:'chkContract',label:t('booking.acceptContract'),req:true},
                  {key:'chkPrivacy', label:t('booking.acceptPrivacy'), req:true},
                  {key:'chkCaution', label:t('booking.acceptCaution'), req:true},
                  {key:'chkMarketing',label:t('booking.acceptMarketing'),req:false},
                ].map(c=>(
                  <label key={c.key} style={{display:'flex',alignItems:'flex-start',gap:'.8rem',marginBottom:'.7rem',cursor:'pointer'}}>
                    <input type="checkbox" checked={form[c.key]} onChange={e=>setForm(f=>({...f,[c.key]:e.target.checked}))} style={{display:'none'}}/>
                    <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${form[c.key]?'var(--green)':'rgba(255,255,255,.2)'}`,background:form[c.key]?'var(--green)':'transparent',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'.7rem',color:form[c.key]?'#fff':'transparent',transition:'all .2s',flexShrink:0,marginTop:'.1rem'}}>✓</div>
                    <span style={{fontSize:'.78rem',color:'rgba(255,255,255,.45)',lineHeight:1.5}}>{c.label} {c.req&&'*'}</span>
                  </label>
                ))}
                {errors.checks && <div className="field-error" style={{marginBottom:'.5rem'}}>{errors.checks}</div>}
                <button className="btn-primary" style={{width:'100%',justifyContent:'center',animation:'none',marginTop:'.5rem'}} onClick={nextStep}>{t('booking.continue')}</button>
              </div>
            )}

            {/* STEP 5 */}
            {step===5 && (
              <div style={s.panel}>
                <h3 style={{color:'#fff',marginBottom:'1.5rem',fontFamily:'var(--font-display)',fontSize:'1.2rem'}}>💳 {t('booking.payment')}</h3>
                <div style={{background:'rgba(212,168,67,.08)',border:'1px solid rgba(212,168,67,.2)',borderRadius:12,padding:'1rem 1.2rem',marginBottom:'1.2rem'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'.6rem',marginBottom:'.5rem'}}>
                    <span>🔒</span><span style={{fontSize:'.82rem',fontWeight:700,color:'var(--gold)'}}>{t('booking.cautionInfo')}</span>
                  </div>
                  <div style={{fontSize:'.75rem',color:'rgba(255,255,255,.45)',lineHeight:1.6}}>
                    {t('booking.cautionDesc')}
                    {form.qty_classic>0 && <><br/>• {t('booking.cautionClassic')}: <strong style={{color:'var(--gold)'}}>€{cautionSettings.classic||100} × {form.qty_classic} = €{form.qty_classic*(cautionSettings.classic||100)}</strong></>}
                    {form.qty_ebike>0   && <><br/>• {t('booking.cautionEbike')}: <strong style={{color:'var(--gold)'}}>€{cautionSettings.ebike||200} × {form.qty_ebike} = €{form.qty_ebike*(cautionSettings.ebike||200)}</strong></>}
                    <br/><strong style={{color:'var(--gold)'}}>Totale cauzione: €{cautionTotal}</strong>
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'.5rem',marginBottom:'1rem'}}>
                  {[
                    {id:'card',emoji:'💳',label:t('payment.card')},
                    {id:'apple',emoji:'🍎',label:t('payment.applePay')},
                    {id:'google',emoji:'🔵',label:t('payment.googlePay')},
                    {id:'paypal',emoji:'🅿️',label:t('payment.paypal')},
                    {id:'transfer',emoji:'🏦',label:t('payment.transfer')},
                  ].map(m=>(
                    <div key={m.id} onClick={()=>setForm(f=>({...f,payMethod:m.id}))}
                      style={{background:form.payMethod===m.id?'rgba(39,174,96,.1)':'rgba(255,255,255,.05)',border:`1.5px solid ${form.payMethod===m.id?'#27AE60':'rgba(255,255,255,.1)'}`,borderRadius:10,padding:'.7rem .4rem',textAlign:'center',cursor:'pointer',transition:'all .2s'}}>
                      <div style={{fontSize:'1.4rem'}}>{m.emoji}</div>
                      <div style={{fontSize:'.68rem',fontWeight:600,color:form.payMethod===m.id?'#fff':'rgba(255,255,255,.6)',marginTop:'.3rem'}}>{m.label}</div>
                    </div>
                  ))}
                </div>
                {form.payMethod==='transfer' && (
                  <div style={{background:'rgba(39,174,96,.06)',border:'1px solid rgba(39,174,96,.2)',borderRadius:10,padding:'.8rem 1rem',fontSize:'.78rem',color:'rgba(255,255,255,.5)',marginBottom:'1rem'}}>
                    ⚡ Bonifico istantaneo: IBAN e dati bancari ti verranno inviati via email. La prenotazione sarà confermata entro pochi minuti dalla ricezione.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SUMMARY */}
          <div style={{position:'sticky',top:'6rem'}}>
            <div style={{background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.1)',borderRadius:20,padding:'1.8rem',marginBottom:'1rem'}}>
              <div style={{fontFamily:'var(--font-display)',fontSize:'1.3rem',color:'#fff',paddingBottom:'1rem',borderBottom:'1px solid rgba(255,255,255,.08)',marginBottom:'1.2rem'}}>🧾 {t('booking.summary')}</div>
              {[
                {label:t('booking.date'),      val:form.date},
                {label:t('booking.duration'),  val:durLabels[form.duration]},
                {label:'🚲 Classiche',         val:form.qty_classic>0?`${form.qty_classic} × €${getPrice('classic',form.duration)}`:'—'},
                {label:'⚡ E-Bike',            val:form.qty_ebike>0?`${form.qty_ebike} × €${getPrice('ebike',form.duration)}`:'—'},
              ].map(r=>(
                <div key={r.label} style={{display:'flex',justifyContent:'space-between',fontSize:'.85rem',color:'rgba(255,255,255,.5)',marginBottom:'.6rem'}}>
                  <span>{r.label}</span><span style={{color:'rgba(255,255,255,.7)',fontWeight:500}}>{r.val}</span>
                </div>
              ))}
              {Object.entries(form.selectedOpts).filter(([,v])=>v).map(([id])=>{
                const opt=optionals.find(o=>o.id===parseInt(id)); if(!opt) return null;
                return <div key={id} style={{display:'flex',justifyContent:'space-between',fontSize:'.85rem',color:'rgba(255,255,255,.5)',marginBottom:'.6rem'}}><span>{opt.name}</span><span style={{color:'rgba(255,255,255,.7)'}}>{opt.price>0?`${totalBikes} × €${opt.price}`:'Gratis'}</span></div>;
              })}
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'1.1rem',fontWeight:700,color:'#fff',paddingTop:'.8rem',marginTop:'.4rem',borderTop:'1px solid rgba(255,255,255,.12)'}}>
                <span>{t('booking.total')}</span><span style={{color:'var(--green)',fontSize:'1.3rem'}}>€{total.toFixed(2)}</span>
              </div>
              {cautionTotal>0 && (
                <div style={{display:'flex',justifyContent:'space-between',fontSize:'.85rem',color:'var(--gold)',marginTop:'.6rem'}}>
                  <span>🔒 {t('booking.caution')}</span><span>€{cautionTotal}</span>
                </div>
              )}
            </div>
            {step===5 && (
              <>
                <button className="btn-primary" style={{width:'100%',justifyContent:'center',padding:'1.1rem',borderRadius:14,fontSize:'1rem'}} onClick={submitBooking} disabled={loading}>
                  {loading?'⏳ Elaborazione...':`🚲 ${t('booking.confirmPay')}`}
                </button>
                <p style={{textAlign:'center',fontSize:'.72rem',color:'rgba(255,255,255,.2)',marginTop:'.7rem'}}>🔒 {t('booking.secure')}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* SUCCESS MODAL */}
      {showSuccess && (
        <div className="modal-overlay" onClick={()=>setShowSuccess(false)}>
          <div style={{background:'var(--dark2)',border:'1px solid rgba(39,174,96,.25)',borderRadius:26,padding:'3rem 2.5rem',textAlign:'center',maxWidth:440,width:'90%',animation:'popIn .4s cubic-bezier(.34,1.56,.64,1)'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:'4rem',marginBottom:'1rem'}}>🎉</div>
            <h3 style={{fontFamily:'var(--font-display)',fontSize:'2rem',color:'#fff',marginBottom:'.7rem'}}>{t('success.title')}</h3>
            <p style={{color:'rgba(255,255,255,.45)',fontSize:'.9rem',lineHeight:1.7,marginBottom:'.5rem'}}>{t('success.sub')}</p>
            {bookingRef && <p style={{color:'var(--green)',fontWeight:700,fontSize:'1.1rem',marginBottom:'1.5rem'}}>Ref: {bookingRef}</p>}
            <button className="btn-primary" style={{animation:'none'}} onClick={()=>setShowSuccess(false)}>{t('success.close')}</button>
          </div>
        </div>
      )}
    </section>
  );
}

// ── FEATURES ──
function Features({ t }) {
  const feats = [
    {icon:'📍',k:'location'},{icon:'⚡',k:'ebike'},{icon:'👨‍👩‍👧',k:'family'},
    {icon:'📄',k:'contract'},{icon:'💳',k:'payments'},{icon:'🗺️',k:'map'},
  ];
  return (
    <section style={{padding:'5rem 1.5rem',background:'var(--cream2)'}}>
      <div className="container">
        <div className="section-eyebrow">{t('features.eyebrow')}</div>
        <h2 className="section-title">{t('features.title')}</h2>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'1.2rem',marginTop:'3rem'}}>
          {feats.map(f=>(
            <div key={f.k} style={{background:'#fff',borderRadius:18,padding:'1.8rem 1.5rem',boxShadow:'0 2px 16px rgba(0,0,0,.05)',borderTop:'3px solid var(--green)',transition:'transform .2s'}}
              onMouseEnter={e=>e.currentTarget.style.transform='translateY(-3px)'}
              onMouseLeave={e=>e.currentTarget.style.transform='translateY(0)'}>
              <div style={{fontSize:'2rem',marginBottom:'.9rem'}}>{f.icon}</div>
              <div style={{fontFamily:'var(--font-display)',fontSize:'1.15rem',fontWeight:600,marginBottom:'.4rem'}}>{t(`features.${f.k}`)}</div>
              <p style={{color:'var(--gray)',fontSize:'.875rem',lineHeight:1.65}}>{t(`features.${f.k}Desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── WHERE ──
function Where({ t, settings }) {
  return (
    <section id="where" style={{padding:'5rem 1.5rem',background:'var(--dark3)',color:'#fff',textAlign:'center'}}>
      <div className="container">
        <div className="section-eyebrow" style={{color:'var(--green)'}}>{t('where.eyebrow')}</div>
        <h2 className="section-title" style={{color:'#fff'}}>{t('where.title')}</h2>
        <div style={{background:'linear-gradient(135deg,rgba(39,174,96,.06),rgba(39,174,96,.02))',border:'1px solid rgba(255,255,255,.08)',borderRadius:20,height:280,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'1rem',margin:'2.5rem 0'}}>
          <div style={{fontSize:'3rem',animation:'bounce 2s ease infinite'}}>📍</div>
          <div style={{fontFamily:'var(--font-display)',fontSize:'1.6rem',color:'#fff'}}>{t('where.address')}</div>
          <div style={{color:'rgba(255,255,255,.4)',fontSize:'.9rem'}}>{t('where.addressSub')}</div>
        </div>
        <div style={{display:'flex',justifyContent:'center',gap:'.8rem',flexWrap:'wrap'}}>
          {[
            {icon:'🕗',txt:t('where.open')},
            {icon:'📅',txt:t('where.daily')},
            {icon:'📞',txt:settings?.business_phone||'+39 055 000 0000'},
            {icon:'✉️',txt:settings?.business_email||'info@bikerentflorence.it'},
            {icon:'💬',txt:t('where.whatsapp')},
          ].map(c=>(
            <div key={c.txt} style={{background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.1)',borderRadius:'100px',padding:'.55rem 1.3rem',fontSize:'.85rem',color:'rgba(255,255,255,.7)',display:'flex',alignItems:'center',gap:'.5rem'}}>
              {c.icon} {c.txt}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── FOOTER ──
function Footer({ t }) {
  return (
    <footer style={{background:'var(--dark)',borderTop:'1px solid rgba(255,255,255,.06)',padding:'3rem 1.5rem'}}>
      <div className="container">
        <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:'2rem',marginBottom:'2rem'}}>
          <div>
            <div style={{fontFamily:'var(--font-display)',fontSize:'1.4rem',color:'#fff',marginBottom:'.6rem'}}>Bike Rent <span style={{color:'var(--green)'}}>Florence</span></div>
            <p style={{fontSize:'.82rem',color:'rgba(255,255,255,.3)',lineHeight:1.7,maxWidth:260}}>{t('footer.tagline')}</p>
          </div>
          <div>
            <h5 style={{fontSize:'.72rem',fontWeight:700,letterSpacing:'.15em',textTransform:'uppercase',color:'rgba(255,255,255,.3)',marginBottom:'1rem'}}>{t('footer.links')}</h5>
            {['#bikes','#prenota','#where'].map(h=>(
              <a key={h} href={h} style={{display:'block',fontSize:'.83rem',color:'rgba(255,255,255,.4)',textDecoration:'none',marginBottom:'.5rem',transition:'color .2s'}} onMouseEnter={e=>e.target.style.color='var(--green)'} onMouseLeave={e=>e.target.style.color='rgba(255,255,255,.4)'}>{h.replace('#','').replace('-',' ')}</a>
            ))}
          </div>
          <div>
            <h5 style={{fontSize:'.72rem',fontWeight:700,letterSpacing:'.15em',textTransform:'uppercase',color:'rgba(255,255,255,.3)',marginBottom:'1rem'}}>{t('footer.legal')}</h5>
            {['privacy','terms','cookie'].map(k=>(
              <a key={k} href="#" style={{display:'block',fontSize:'.83rem',color:'rgba(255,255,255,.4)',textDecoration:'none',marginBottom:'.5rem',transition:'color .2s'}} onMouseEnter={e=>e.target.style.color='var(--green)'} onMouseLeave={e=>e.target.style.color='rgba(255,255,255,.4)'}>{t(`footer.${k}`)}</a>
            ))}
          </div>
        </div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',paddingTop:'1.5rem',borderTop:'1px solid rgba(255,255,255,.06)',flexWrap:'wrap',gap:'.5rem'}}>
          <p style={{fontSize:'.75rem',color:'rgba(255,255,255,.2)'}}>© {new Date().getFullYear()} <span style={{color:'var(--green)'}}>Bike Rent Florence</span> · {t('footer.copyright')}</p>
          <div style={{display:'flex',gap:'.6rem'}}>
            {['SSL','GDPR','PCI DSS'].map(b=><span key={b} style={{background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.08)',borderRadius:6,padding:'.3rem .7rem',fontSize:'.65rem',color:'rgba(255,255,255,.3)',fontWeight:600,letterSpacing:'.06em'}}>{b}</span>)}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── COOKIE BANNER ──
function CookieBanner({ t }) {
  const [show, setShow] = useState(!localStorage.getItem('brf_cookie'));
  if (!show) return null;
  return (
    <div style={{position:'fixed',bottom:'1.5rem',left:'50%',transform:'translateX(-50%)',zIndex:550,background:'var(--dark)',border:'1px solid rgba(255,255,255,.1)',borderRadius:16,padding:'1.2rem 1.8rem',maxWidth:560,width:'calc(100% - 2rem)',display:'flex',alignItems:'center',gap:'1.5rem',boxShadow:'0 8px 40px rgba(0,0,0,.5)',animation:'slideUp .4s ease'}}>
      <p style={{fontSize:'.82rem',color:'rgba(255,255,255,.55)',lineHeight:1.6,flex:1}}>{t('cookie.text')}</p>
      <div style={{display:'flex',gap:'.6rem',flexShrink:0}}>
        <button onClick={()=>{localStorage.setItem('brf_cookie','1');setShow(false);}} style={{background:'var(--green)',border:'none',color:'var(--dark)',fontWeight:700,fontSize:'.8rem',padding:'.55rem 1.2rem',borderRadius:8,cursor:'pointer'}}>{t('cookie.accept')}</button>
        <button onClick={()=>setShow(false)} style={{background:'transparent',border:'1px solid rgba(255,255,255,.15)',color:'rgba(255,255,255,.5)',fontSize:'.8rem',padding:'.55rem 1rem',borderRadius:8,cursor:'pointer'}}>{t('cookie.decline')}</button>
      </div>
    </div>
  );
}

// ── BANNER ──
function SiteBanner({ settings }) {
  if (!settings || settings.banner_active !== 'true') return null;
  const colors = {info:{bg:'rgba(39,174,96,.12)',border:'rgba(39,174,96,.25)',color:'var(--green)'},gold:{bg:'rgba(212,168,67,.12)',border:'rgba(212,168,67,.25)',color:'var(--gold)'},red:{bg:'rgba(231,76,60,.12)',border:'rgba(231,76,60,.25)',color:'var(--red)'}};
  const c = colors[settings.banner_color]||colors.info;
  return <div style={{position:'fixed',top:0,left:0,right:0,zIndex:600,background:c.bg,borderBottom:`1px solid ${c.border}`,color:c.color,textAlign:'center',padding:'.5rem 1rem',fontSize:'.82rem',fontWeight:600}}>{settings.banner_text}</div>;
}

// ══════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════
export default function HomePage() {
  const { t } = useTranslation();
  const [settings, setSettings]     = useState(null);
  const [rates, setRates]           = useState([]);
  const [optionals, setOptionals]   = useState([]);
  const [availability, setAvailability] = useState({ classic: 99, ebike: 99 });
  const [cautionSettings, setCautionSettings] = useState({ classic: 100, ebike: 200 });

  useEffect(() => {
    // Carica tutto in parallelo
    Promise.all([
      publicApi.getSettings(),
      publicApi.getRates(),
      publicApi.getOptionals(),
      publicApi.getInfo(),
    ]).then(([sRes, rRes, oRes, iRes]) => {
      setSettings(sRes.data);
      setRates(rRes.data.filter(r => r.active !== 0));
      setOptionals(oRes.data.filter(o => o.active !== 0));
      setCautionSettings({
        classic: parseFloat(sRes.data.caution_classic || 100),
        ebike:   parseFloat(sRes.data.caution_ebike   || 200),
      });
      const avail = iRes.data.availability || [];
      const c = avail.find(a => a.type === 'classic');
      const e = avail.find(a => a.type === 'ebike');
      setAvailability({ classic: parseInt(c?.available || 99), ebike: parseInt(e?.available || 99) });
    }).catch(err => {
      console.warn('API non disponibile, uso dati di fallback', err.message);
      // Dati di esempio per sviluppo locale
      setRates([
        {id:1,bike_type:'classic',duration:'1h', price:8, active:1},
        {id:2,bike_type:'classic',duration:'2h', price:16,active:1},
        {id:3,bike_type:'classic',duration:'4h', price:22,active:1},
        {id:4,bike_type:'classic',duration:'day',price:25,active:1},
        {id:5,bike_type:'ebike',  duration:'1h', price:15,active:1},
        {id:6,bike_type:'ebike',  duration:'2h', price:30,active:1},
        {id:7,bike_type:'ebike',  duration:'4h', price:40,active:1},
        {id:8,bike_type:'ebike',  duration:'day',price:45,active:1},
      ]);
      setOptionals([
        {id:1,name:'Seggiolino anteriore bimbo',description:'Fino a 15 kg · max 3 anni',price:3,active:1},
        {id:2,name:'Portabimbo posteriore',    description:'Fino a 22 kg · max 6 anni',price:4,active:1},
        {id:3,name:'Casco di sicurezza',       description:'Adulto o bambino · CE EN1078',price:2,active:1},
        {id:4,name:'Lucchetto extra',          description:'Combinazione 4 cifre',       price:1,active:1},
        {id:5,name:'Mappa itinerari Firenze',  description:'Percorsi del nostro staff',  price:0,active:1},
      ]);
    });
  }, []);

  const hasBanner = settings?.banner_active === 'true';

  return (
    <div>
      <SiteBanner settings={settings}/>
      <Nav t={t}/>
      <div style={{paddingTop: hasBanner ? 36 : 0}}>
        <Hero t={t}/>
        <BikesSection t={t} settings={settings} rates={rates} availability={availability}/>
        <BookingSection t={t} settings={settings} rates={rates} optionals={optionals} availability={availability} cautionSettings={cautionSettings}/>
        <Features t={t}/>
        <Where t={t} settings={settings}/>
        <Footer t={t}/>
      </div>
      <CookieBanner t={t}/>
    </div>
  );
}
